import React, { useState, useEffect, useMemo, memo } from 'react';
import { Card, CardHeader, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { 
  ChevronUp, ChevronDown, Trash2, Plus, Link2, ArrowRight, 
  Database, Settings, Eye, Code, ChevronRight, Layers,
  Calculator, Filter, Sparkles, FileCode, ToggleLeft, ToggleRight,
  AlertCircle, Info
} from 'lucide-react';
import api from '../services/api';

// Aggregation functions available
const AGGREGATIONS = [
  { value: 'none', label: 'Direct (no aggregation)' },
  { value: 'SUM', label: 'SUM()' },
  { value: 'COUNT', label: 'COUNT()' },
  { value: 'COUNT_DISTINCT', label: 'COUNT(DISTINCT)' },
  { value: 'AVG', label: 'AVG()' },
  { value: 'MIN', label: 'MIN()' },
  { value: 'MAX', label: 'MAX()' },
  { value: 'SAFE_DIVIDE', label: 'SAFE_DIVIDE()' },
];

// Date functions
const DATE_FUNCTIONS = [
  { value: 'none', label: 'None' },
  { value: 'DATE_TRUNC_DAY', label: 'DATE_TRUNC(DAY)' },
  { value: 'DATE_TRUNC_WEEK', label: 'DATE_TRUNC(ISOWEEK)' },
  { value: 'DATE_TRUNC_MONTH', label: 'DATE_TRUNC(MONTH)' },
  { value: 'DATE_TRUNC_QUARTER', label: 'DATE_TRUNC(QUARTER)' },
  { value: 'DATE_TRUNC_YEAR', label: 'DATE_TRUNC(YEAR)' },
  { value: 'EXTRACT_YEAR', label: 'EXTRACT(YEAR)' },
  { value: 'EXTRACT_MONTH', label: 'EXTRACT(MONTH)' },
  { value: 'EXTRACT_DAY', label: 'EXTRACT(DAY)' },
];

// Data types
const DATA_TYPES = [
  { value: 'STRING', label: 'STRING' },
  { value: 'INT64', label: 'INT64' },
  { value: 'FLOAT64', label: 'FLOAT64' },
  { value: 'NUMERIC', label: 'NUMERIC' },
  { value: 'DATE', label: 'DATE' },
  { value: 'TIMESTAMP', label: 'TIMESTAMP' },
  { value: 'BOOL', label: 'BOOL' },
];

// Join types
const JOIN_TYPES = [
  { value: 'INNER', label: 'INNER JOIN' },
  { value: 'LEFT', label: 'LEFT JOIN' },
  { value: 'RIGHT', label: 'RIGHT JOIN' },
  { value: 'FULL', label: 'FULL OUTER' },
];

// Collapsible Section Component
const Section = ({ title, number, icon: Icon, children, defaultOpen = true, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border rounded-lg bg-white overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-sm font-medium">
            {number}
          </div>
          {Icon && <Icon className="w-4 h-4 text-amber-600" />}
          <span className="font-medium text-sm">{title}</span>
          {badge && <Badge variant="secondary" className="text-xs">{badge}</Badge>}
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {isOpen && <div className="px-4 pb-4 pt-2 border-t bg-slate-50/50">{children}</div>}
    </div>
  );
};

// Field Row Component with Expression Builder
const FieldRow = ({ 
  field, 
  index, 
  availableFields, 
  onUpdate, 
  onRemove,
  showAggregation = true 
}) => {
  const [showAdvanced, setShowAdvanced] = useState(!!field.custom_expression);
  
  return (
    <div className="bg-white p-3 rounded-lg border space-y-2">
      <div className="flex gap-2 items-start">
        {/* Source Field Selection */}
        <div className="flex-1 min-w-[180px]">
          <Select 
            value={field.source_field || ""} 
            onValueChange={(v) => onUpdate(index, { 
              source_field: v, 
              target_field: field.target_field || v.split('.').pop() 
            })}
          >
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Select field" />
            </SelectTrigger>
            <SelectContent>
              {availableFields.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  <span className="font-mono text-xs">{f.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Aggregation (if enabled) */}
        {showAggregation && (
          <div className="w-[140px]">
            <Select 
              value={field.aggregation || "none"} 
              onValueChange={(v) => onUpdate(index, { aggregation: v })}
            >
              <SelectTrigger className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AGGREGATIONS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Date Function */}
        <div className="w-[150px]">
          <Select 
            value={field.date_function || "none"} 
            onValueChange={(v) => onUpdate(index, { date_function: v })}
          >
            <SelectTrigger className="text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_FUNCTIONS.map((d) => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <ArrowRight className="w-4 h-4 text-muted-foreground mt-2.5 shrink-0" />
        
        {/* Target Field Name */}
        <div className="w-[140px]">
          <Input
            value={field.target_field || ""}
            onChange={(e) => onUpdate(index, { target_field: e.target.value })}
            placeholder="Output name"
            className="text-sm"
          />
        </div>
        
        {/* Data Type */}
        <div className="w-[100px]">
          <Select 
            value={field.data_type || "STRING"} 
            onValueChange={(v) => onUpdate(index, { data_type: v })}
          >
            <SelectTrigger className="text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATA_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Actions */}
        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-9 w-9"
            onClick={() => setShowAdvanced(!showAdvanced)}
            title="Custom expression"
          >
            <Code className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => onRemove(index)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Advanced: Custom Expression */}
      {showAdvanced && (
        <div className="pt-2 border-t">
          <Label className="text-xs text-muted-foreground mb-1 block">Custom SQL Expression (overrides above)</Label>
          <Input
            value={field.custom_expression || ""}
            onChange={(e) => onUpdate(index, { custom_expression: e.target.value })}
            placeholder="e.g., SAFE_DIVIDE(SUM(profit), NULLIF(SUM(revenue), 0))"
            className="font-mono text-sm"
          />
        </div>
      )}
    </div>
  );
};

// Main Gold Stage Card V2
const GoldStageCardV2 = memo(({ 
  stage, 
  stageIndex,
  silverTables,
  goldTables,
  silverFieldsByTable,
  goldFieldsByTable,
  bigqueryConnectionId,
  existingBigQueryGoldTables, // NEW: Tables that exist in BigQuery gold_layer
  onUpdate, 
  onRemove,
  onImportSQL // NEW: Callback to open SQL import modal
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('simple');
  const [fetchedGoldFields, setFetchedGoldFields] = useState({});
  const [showSqlPreview, setShowSqlPreview] = useState(false);
  
  // Raw SQL Mode state
  const isRawSqlMode = stage.raw_sql_mode || false;
  const rawSql = stage.raw_sql || '';
  const autoInjectTenantFilter = stage.auto_inject_tenant_filter !== false; // Default true
  
  const sourceTables = stage.source_tables || [];
  const joinConditions = stage.join_conditions || [];
  const fields = stage.fields || [];
  const groupByFields = stage.group_by_fields || [];
  const calculatedFields = stage.calculated_fields || [];
  
  // Toggle Raw SQL Mode
  const toggleRawSqlMode = () => {
    onUpdate({ ...stage, raw_sql_mode: !isRawSqlMode });
  };
  
  // Update Raw SQL
  const updateRawSql = (sql) => {
    onUpdate({ ...stage, raw_sql: sql });
  };
  
  // Extract target table name from raw SQL
  const extractTargetTableFromSql = (sql) => {
    const match = sql.match(/CREATE\s+(?:OR\s+REPLACE\s+)?TABLE\s+`?([^`\s(]+)`?/i);
    if (match) {
      const fullName = match[1];
      // Extract just the table name (after the last dot)
      const parts = fullName.split('.');
      return parts[parts.length - 1];
    }
    return null;
  };
  
  // Auto-extract target table name from raw SQL
  useEffect(() => {
    if (isRawSqlMode && rawSql && !stage.target_table) {
      const extractedTable = extractTargetTableFromSql(rawSql);
      if (extractedTable) {
        onUpdate({ ...stage, target_table: extractedTable });
      }
    }
  }, [isRawSqlMode, rawSql]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Determine operation mode based on source tables
  const operationMode = useMemo(() => {
    const unionCount = sourceTables.filter(st => st.role === 'union').length;
    const joinCount = sourceTables.filter(st => st.role !== 'union').length;
    if (unionCount > 1 && joinCount > 0) return 'mixed';
    if (unionCount > 1) return 'union';
    if (joinCount > 1) return 'join';
    return 'single';
  }, [sourceTables]);
  
  // Fetch Gold table columns from BigQuery when needed
  useEffect(() => {
    const fetchMissingGoldFields = async () => {
      for (const st of sourceTables) {
        if (st.source_layer === 'gold' && st.table_name && bigqueryConnectionId) {
          const existingFields = (goldFieldsByTable || {})[st.table_name] || [];
          const cachedFields = fetchedGoldFields[st.table_name] || [];
          
          if (existingFields.length === 0 && cachedFields.length === 0) {
            try {
              const response = await api.getGoldTableColumns(bigqueryConnectionId, st.table_name);
              if (response.columns && response.columns.length > 0) {
                setFetchedGoldFields(prev => ({
                  ...prev,
                  [st.table_name]: response.columns.map(c => ({
                    name: c.name || c.column_name,
                    target_field: c.name || c.column_name,
                    data_type: c.data_type
                  }))
                }));
              }
            } catch (err) {
              console.warn(`Could not fetch fields for Gold table ${st.table_name}:`, err);
            }
          }
        }
      }
    };
    fetchMissingGoldFields();
  }, [sourceTables, goldFieldsByTable, bigqueryConnectionId, fetchedGoldFields]);
  
  // Build available fields from all source tables
  const availableFields = useMemo(() => {
    const result = [];
    sourceTables.forEach(st => {
      const layer = st.source_layer || 'silver';
      let tableFields = [];
      
      if (layer === 'gold') {
        tableFields = (goldFieldsByTable || {})[st.table_name] || fetchedGoldFields[st.table_name] || [];
      } else {
        tableFields = silverFieldsByTable[st.table_name] || [];
      }
      
      tableFields.forEach(f => {
        result.push({
          value: `${st.alias}.${f.name || f.target_field}`,
          label: `${st.alias}.${f.name || f.target_field}`,
          table: st.table_name,
          alias: st.alias,
          field: f.name || f.target_field,
          data_type: f.data_type || f.type
        });
      });
    });
    return result;
  }, [sourceTables, silverFieldsByTable, goldFieldsByTable, fetchedGoldFields]);
  
  // Get fields for a specific alias
  const getFieldsForAlias = (alias) => {
    const table = sourceTables.find(st => st.alias === alias);
    if (!table) return [];
    const layer = table.source_layer || 'silver';
    if (layer === 'gold') {
      return (goldFieldsByTable || {})[table.table_name] || fetchedGoldFields[table.table_name] || [];
    }
    return silverFieldsByTable[table.table_name] || [];
  };
  
  // Generate SQL Preview
  const sqlPreview = useMemo(() => {
    if (sourceTables.length === 0) return '-- Select source tables first';
    
    const targetTable = stage.target_table || 'target_table';
    let sql = `CREATE OR REPLACE TABLE \`gold_layer.${targetTable}\`\n`;
    
    // Partitioning
    if (stage.partition_by) {
      sql += `PARTITION BY ${stage.partition_by}\n`;
    }
    // Clustering
    if (stage.cluster_by) {
      sql += `CLUSTER BY ${stage.cluster_by}\n`;
    }
    
    sql += 'AS\nSELECT\n';
    
    // Fields
    const fieldLines = [];
    fields.forEach(f => {
      if (f.custom_expression) {
        fieldLines.push(`  ${f.custom_expression} AS ${f.target_field || 'field'}`);
      } else if (f.source_field) {
        let expr = f.source_field;
        
        // Apply date function
        if (f.date_function && f.date_function !== 'none') {
          if (f.date_function.startsWith('DATE_TRUNC_')) {
            const period = f.date_function.replace('DATE_TRUNC_', '');
            expr = `DATE_TRUNC(${expr}, ${period})`;
          } else if (f.date_function.startsWith('EXTRACT_')) {
            const part = f.date_function.replace('EXTRACT_', '');
            expr = `EXTRACT(${part} FROM ${expr})`;
          }
        }
        
        // Apply aggregation
        if (f.aggregation && f.aggregation !== 'none') {
          if (f.aggregation === 'COUNT_DISTINCT') {
            expr = `COUNT(DISTINCT ${expr})`;
          } else {
            expr = `${f.aggregation}(${expr})`;
          }
        }
        
        fieldLines.push(`  ${expr} AS ${f.target_field || f.source_field.split('.').pop()}`);
      }
    });
    
    // Calculated fields
    calculatedFields.forEach(cf => {
      if (cf.expression && cf.name) {
        fieldLines.push(`  ${cf.expression} AS ${cf.name}`);
      }
    });
    
    if (fieldLines.length === 0) {
      fieldLines.push('  -- Add fields above');
    }
    
    sql += fieldLines.join(',\n') + '\n';
    
    // FROM clause
    if (operationMode === 'union' || operationMode === 'mixed') {
      const unionTables = sourceTables.filter(st => st.role === 'union');
      sql += 'FROM (\n';
      unionTables.forEach((ut, idx) => {
        const layer = ut.source_layer === 'gold' ? 'gold_layer' : 'silver_layer';
        sql += `  SELECT * FROM \`${layer}.${ut.table_name}\``;
        if (idx < unionTables.length - 1) sql += '\n  UNION ALL\n';
      });
      sql += '\n) AS unioned\n';
    } else {
      const firstTable = sourceTables[0];
      if (firstTable) {
        const layer = firstTable.source_layer === 'gold' ? 'gold_layer' : 'silver_layer';
        sql += `FROM \`${layer}.${firstTable.table_name}\` AS ${firstTable.alias}\n`;
      }
    }
    
    // JOINs
    if (operationMode === 'join' || operationMode === 'mixed') {
      const joinTablesToUse = operationMode === 'mixed' 
        ? sourceTables.filter(st => st.role !== 'union')
        : sourceTables.slice(1);
      
      joinConditions.forEach((jc, idx) => {
        if (jc.left_table && jc.right_table && jc.left_field && jc.right_field) {
          const rightTable = sourceTables.find(st => st.alias === jc.right_table);
          if (rightTable) {
            const layer = rightTable.source_layer === 'gold' ? 'gold_layer' : 'silver_layer';
            sql += `${jc.join_type || 'LEFT'} JOIN \`${layer}.${rightTable.table_name}\` AS ${rightTable.alias}\n`;
            sql += `  ON ${jc.left_table}.${jc.left_field} = ${jc.right_table}.${jc.right_field}\n`;
          }
        }
      });
    }
    
    // WHERE
    if (stage.filter_condition) {
      sql += `WHERE ${stage.filter_condition}\n`;
    }
    
    // GROUP BY
    if (groupByFields.length > 0) {
      sql += `GROUP BY ${groupByFields.join(', ')}\n`;
    } else {
      // Auto-detect GROUP BY from non-aggregated fields
      const nonAggFields = fields
        .filter(f => !f.aggregation || f.aggregation === 'none')
        .filter(f => !f.custom_expression)
        .map(f => f.source_field || f.target_field)
        .filter(Boolean);
      if (nonAggFields.length > 0 && fields.some(f => f.aggregation && f.aggregation !== 'none')) {
        sql += `GROUP BY ${nonAggFields.join(', ')}\n`;
      }
    }
    
    return sql;
  }, [stage, sourceTables, fields, calculatedFields, joinConditions, operationMode, groupByFields]);
  
  // Handlers
  const addSourceTable = () => {
    const alias = `t${sourceTables.length + 1}`;
    onUpdate({
      ...stage,
      source_tables: [...sourceTables, { table_name: "", alias, role: "join", source_layer: "silver", union_fields: [] }]
    });
  };
  
  const updateSourceTable = (index, updates) => {
    const newTables = [...sourceTables];
    newTables[index] = { ...newTables[index], ...updates };
    onUpdate({ ...stage, source_tables: newTables });
  };
  
  const removeSourceTable = (index) => {
    onUpdate({ 
      ...stage, 
      source_tables: sourceTables.filter((_, i) => i !== index),
      join_conditions: joinConditions.filter(j => 
        j.left_table !== sourceTables[index]?.alias && 
        j.right_table !== sourceTables[index]?.alias
      )
    });
  };
  
  const addField = () => {
    onUpdate({
      ...stage,
      fields: [...fields, { source_field: "", target_field: "", aggregation: "none", date_function: "none", data_type: "STRING" }]
    });
  };
  
  const updateField = (index, updates) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    onUpdate({ ...stage, fields: newFields });
  };
  
  const removeField = (index) => {
    onUpdate({ ...stage, fields: fields.filter((_, i) => i !== index) });
  };
  
  const addAllFields = () => {
    const newFields = availableFields.map(f => ({
      source_field: f.value,
      target_field: f.field,
      aggregation: "none",
      date_function: "none",
      data_type: f.data_type || "STRING"
    }));
    onUpdate({ ...stage, fields: newFields });
  };
  
  const addJoinCondition = () => {
    onUpdate({
      ...stage,
      join_conditions: [...joinConditions, { 
        left_table: sourceTables[0]?.alias || "t1", 
        left_field: "", 
        right_table: sourceTables[1]?.alias || "t2", 
        right_field: "",
        join_type: "LEFT"
      }]
    });
  };
  
  const updateJoinCondition = (index, updates) => {
    const newJoins = [...joinConditions];
    newJoins[index] = { ...newJoins[index], ...updates };
    onUpdate({ ...stage, join_conditions: newJoins });
  };
  
  const removeJoinCondition = (index) => {
    onUpdate({ ...stage, join_conditions: joinConditions.filter((_, i) => i !== index) });
  };
  
  const addCalculatedField = () => {
    onUpdate({
      ...stage,
      calculated_fields: [...calculatedFields, { name: "", expression: "" }]
    });
  };
  
  const updateCalculatedField = (index, updates) => {
    const newCalcFields = [...calculatedFields];
    newCalcFields[index] = { ...newCalcFields[index], ...updates };
    onUpdate({ ...stage, calculated_fields: newCalcFields });
  };
  
  const removeCalculatedField = (index) => {
    onUpdate({ ...stage, calculated_fields: calculatedFields.filter((_, i) => i !== index) });
  };
  
  // Combine all available tables (Silver + Gold from pipeline + Gold from BigQuery)
  const allSourceOptions = useMemo(() => {
    const options = [];
    
    // Silver tables from pipeline
    (silverTables || []).forEach(t => {
      options.push({ value: t, label: `silver_layer.${t}`, layer: 'silver', section: 'Silver Layer' });
    });
    
    // Gold tables from previous stages
    (goldTables || []).forEach(t => {
      options.push({ value: `gold:${t}`, label: `gold_layer.${t}`, layer: 'gold', section: 'Gold Layer (Pipeline)' });
    });
    
    // Gold tables from BigQuery (existing)
    (existingBigQueryGoldTables || []).forEach(t => {
      // Don't duplicate if already in goldTables
      if (!(goldTables || []).includes(t)) {
        options.push({ value: `gold:${t}`, label: `gold_layer.${t} (existing)`, layer: 'gold', section: 'Gold Layer (BigQuery)' });
      }
    });
    
    return options;
  }, [silverTables, goldTables, existingBigQueryGoldTables]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50/50 to-orange-50/30 shadow-md">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className="bg-amber-500 text-white px-3">Gold</Badge>
                <span className="font-medium">
                  {stage.target_table || 'New Gold Table'}
                </span>
                {isRawSqlMode ? (
                  <Badge variant="outline" className="text-xs bg-purple-50 border-purple-300 text-purple-700">
                    <Code className="w-3 h-3 mr-1" /> Raw SQL
                  </Badge>
                ) : sourceTables.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {operationMode === 'single' ? '1 table' : 
                     operationMode === 'join' ? `Join ${sourceTables.length}` :
                     operationMode === 'union' ? `Union ${sourceTables.length}` :
                     `Mixed ${sourceTables.length}`}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!isRawSqlMode && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setShowSqlPreview(!showSqlPreview); }}
                    className="text-xs"
                  >
                    <Eye className="w-3 h-3 mr-1" /> SQL
                  </Button>
                )}
                {onImportSQL && !isRawSqlMode && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onImportSQL(stageIndex); }}
                    className="text-xs"
                  >
                    <FileCode className="w-3 h-3 mr-1" /> Import
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => { e.stopPropagation(); onRemove(); }}
                  className="text-destructive hover:text-destructive h-8 w-8"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            {/* Mode Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium">Mode:</Label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleRawSqlMode}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      !isRawSqlMode 
                        ? 'bg-amber-500 text-white' 
                        : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Visual Builder
                  </button>
                  <button
                    onClick={toggleRawSqlMode}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      isRawSqlMode 
                        ? 'bg-purple-500 text-white' 
                        : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Code className="w-3 h-3 inline mr-1" />
                    Raw SQL
                  </button>
                </div>
              </div>
              {isRawSqlMode && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Info className="w-3 h-3" />
                  Paste your complete CREATE TABLE SQL
                </div>
              )}
            </div>
            
            {/* RAW SQL MODE */}
            {isRawSqlMode ? (
              <div className="space-y-4">
                {/* Info Banner */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-purple-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-purple-800">
                      <p className="font-medium">Raw SQL Mode</p>
                      <p className="text-purple-600 mt-1">
                        Paste your complete SQL statement. The system will execute it directly:
                      </p>
                      <ul className="text-purple-600 mt-1 ml-4 list-disc text-xs">
                        <li><strong>Cloud (BigQuery)</strong>: Runs as-is</li>
                        <li><strong>Local Export</strong>: Auto-converts to PostgreSQL/Oracle/SQL Server syntax</li>
                      </ul>
                    </div>
                  </div>
                  <div className="text-xs text-purple-600 mt-2 bg-purple-100 p-2 rounded font-mono">
                    <p><strong>Placeholders available:</strong></p>
                    <p className="mt-1">• <code>{'{tenant_id}'}</code> - Replaced with selected tenant ID</p>
                    <p>• <code>{'{unit_id}'}</code> - Replaced with selected unit ID</p>
                  </div>
                </div>
                
                {/* SQL Editor */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">SQL Statement</Label>
                  <textarea
                    value={rawSql}
                    onChange={(e) => updateRawSql(e.target.value)}
                    placeholder={`CREATE OR REPLACE TABLE \`gold_layer.my_table\`
PARTITION BY date_column
CLUSTER BY tenant_id
AS
SELECT
  tenant_id,
  ...
FROM \`silver_layer.source_table\`
WHERE tenant_id = '{tenant_id}'
GROUP BY ...;`}
                    className="w-full h-[400px] p-4 border rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 bg-slate-900 text-slate-100"
                    spellCheck={false}
                  />
                </div>
                
                {/* Target Table Name (auto-extracted or manual) */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Target Table Name</Label>
                    <Input
                      value={stage.target_table || ''}
                      onChange={(e) => onUpdate({ ...stage, target_table: e.target.value })}
                      placeholder="Auto-extracted from SQL or enter manually"
                      className="text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      {stage.target_table ? '✓ Table name set' : 'Will be extracted from SQL'}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">Tenant Filter Handling</Label>
                    <div className="flex items-center gap-2 p-3 bg-white border rounded-lg">
                      <Checkbox
                        id="auto-inject-tenant"
                        checked={autoInjectTenantFilter}
                        onCheckedChange={(checked) => onUpdate({ ...stage, auto_inject_tenant_filter: checked })}
                      />
                      <label htmlFor="auto-inject-tenant" className="text-sm cursor-pointer">
                        Use <code className="bg-slate-100 px-1 rounded">{'{tenant_id}'}</code> placeholder in SQL
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* SQL Validation Hints */}
                {rawSql && (
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                    <Label className="text-xs font-medium text-slate-600">SQL Analysis</Label>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-2">
                        {rawSql.toLowerCase().includes('create') ? (
                          <span className="text-green-600">✓ CREATE statement found</span>
                        ) : (
                          <span className="text-amber-600">⚠ No CREATE statement</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {rawSql.includes('{tenant_id}') ? (
                          <span className="text-green-600">✓ Tenant placeholder found</span>
                        ) : rawSql.toLowerCase().includes('tenant_id') ? (
                          <span className="text-amber-600">⚠ tenant_id found but no placeholder</span>
                        ) : (
                          <span className="text-slate-400">○ No tenant filter</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {rawSql.toLowerCase().includes('partition by') ? (
                          <span className="text-green-600">✓ Partitioning defined</span>
                        ) : (
                          <span className="text-slate-400">○ No partitioning</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {rawSql.toLowerCase().includes('cluster by') ? (
                          <span className="text-green-600">✓ Clustering defined</span>
                        ) : (
                          <span className="text-slate-400">○ No clustering</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* On-Premise Automation: Gold T-SQL */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <Database className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">On-Premise Gold SQL (for Automation Export)</p>
                      <p className="text-blue-600 mt-1 text-xs">
                        Write T-SQL that reads from SILVER_LAYER_* tables and produces the Gold result.
                        This SQL is used in the exported automation script. Uses MERGE for incremental updates.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Gold T-SQL Statement</Label>
                    <textarea
                      value={stage.gold_tsql || ''}
                      onChange={(e) => onUpdate({ ...stage, gold_tsql: e.target.value })}
                      placeholder={`-- Example: Aggregate Silver data into Gold
SELECT
    i.customer_name,
    i.invoice_id,
    COUNT(il.invoice) AS total_lines,
    SUM(TRY_CAST(il.amount AS DECIMAL(18,2))) AS total_amount
FROM [SILVER_LAYER_INVOICES] i
JOIN [SILVER_LAYER_INVOICE_LINES] il
    ON i.invoice_id = il.invoice_id
GROUP BY i.customer_name, i.invoice_id`}
                      className="w-full h-[250px] p-4 border rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 bg-slate-900 text-slate-100"
                      spellCheck={false}
                      data-testid="gold-tsql-editor"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Gold Key Column(s) <span className="text-muted-foreground font-normal">(for MERGE)</span></Label>
                      <Input
                        value={stage.gold_key_column || ''}
                        onChange={(e) => onUpdate({ ...stage, gold_key_column: e.target.value })}
                        placeholder="e.g., customer_name, invoice_id"
                        className="text-sm"
                        data-testid="gold-key-column"
                      />
                      <p className="text-xs text-muted-foreground">Comma-separated. Used as MERGE key in automation.</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Gold Target Table</Label>
                      <Input
                        value={stage.target_table || ''}
                        onChange={(e) => onUpdate({ ...stage, target_table: e.target.value })}
                        placeholder="e.g., SALES_SUMMARY"
                        className="text-sm"
                        data-testid="gold-target-table"
                      />
                      <p className="text-xs text-muted-foreground">Will be created as GOLD_LAYER_[name]</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* VISUAL BUILDER MODE - Original content */}
                {/* SQL Preview Panel */}
                {showSqlPreview && (
                  <div className="bg-slate-900 text-slate-100 p-4 rounded-lg font-mono text-xs overflow-auto max-h-[300px]">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400 text-xs">SQL Preview (auto-generated)</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 text-xs text-slate-400 hover:text-white"
                        onClick={() => navigator.clipboard.writeText(sqlPreview)}
                      >
                        Copy
                      </Button>
                    </div>
                    <pre className="whitespace-pre-wrap">{sqlPreview}</pre>
                  </div>
                )}
                
                {/* Section 1: Source Tables */}
                <Section number="1" title="Source Tables" icon={Database} badge={sourceTables.length > 0 ? `${sourceTables.length} selected` : null}>
                  <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Select tables from Silver or Gold layers. For multiple tables, choose Join or Union.
                </p>
                
                {sourceTables.map((st, idx) => (
                  <div key={idx} className="flex gap-2 items-center flex-wrap bg-white p-2 rounded-lg border">
                    <Badge variant="outline" className={`w-10 justify-center font-mono text-xs ${st.source_layer === 'gold' ? 'bg-amber-50 border-amber-300' : 'bg-slate-50'}`}>
                      {st.alias}
                    </Badge>
                    
                    <Select 
                      value={st.source_layer === 'gold' ? `gold:${st.table_name}` : st.table_name} 
                      onValueChange={(value) => {
                        if (value.startsWith('gold:')) {
                          updateSourceTable(idx, { table_name: value.replace('gold:', ''), source_layer: 'gold' });
                        } else {
                          updateSourceTable(idx, { table_name: value, source_layer: 'silver' });
                        }
                      }}
                    >
                      <SelectTrigger className="flex-1 min-w-[200px] text-sm">
                        <SelectValue placeholder="Select table" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Group by section */}
                        {['Silver Layer', 'Gold Layer (Pipeline)', 'Gold Layer (BigQuery)'].map(section => {
                          const sectionItems = allSourceOptions.filter(o => o.section === section);
                          if (sectionItems.length === 0) return null;
                          return (
                            <React.Fragment key={section}>
                              <div className={`px-2 py-1.5 text-xs font-semibold text-muted-foreground ${section.includes('Gold') ? 'bg-amber-50' : 'bg-slate-50'}`}>
                                {section}
                              </div>
                              {sectionItems.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    
                    {sourceTables.length > 1 && (
                      <Select 
                        value={st.role || "join"} 
                        onValueChange={(value) => updateSourceTable(idx, { role: value })}
                      >
                        <SelectTrigger className="w-24 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="join">
                            <span className="flex items-center gap-1">
                              <Link2 className="w-3 h-3" /> Join
                            </span>
                          </SelectItem>
                          <SelectItem value="union">
                            <span className="flex items-center gap-1">
                              <Layers className="w-3 h-3" /> Union
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeSourceTable(idx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                <Button variant="outline" size="sm" onClick={addSourceTable}>
                  <Plus className="w-4 h-4 mr-1" /> Add Table
                </Button>
              </div>
            </Section>
            
            {/* Section 2: Join Conditions (only if multiple join tables) */}
            {sourceTables.filter(st => st.role !== 'union').length > 1 && (
              <Section number="2" title="Join Conditions" icon={Link2} badge={joinConditions.length > 0 ? `${joinConditions.length} conditions` : null}>
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Define how tables are joined together.
                  </p>
                  
                  {joinConditions.map((jc, idx) => {
                    const leftFields = getFieldsForAlias(jc.left_table);
                    const rightFields = getFieldsForAlias(jc.right_table);
                    
                    return (
                      <div key={idx} className="flex gap-2 items-center flex-wrap bg-white p-2 rounded-lg border">
                        <Select value={jc.join_type || "LEFT"} onValueChange={(v) => updateJoinCondition(idx, { join_type: v })}>
                          <SelectTrigger className="w-[110px] text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {JOIN_TYPES.map((j) => (
                              <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Select value={jc.left_table} onValueChange={(v) => updateJoinCondition(idx, { left_table: v, left_field: "" })}>
                          <SelectTrigger className="w-16 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {sourceTables.map((st) => (
                              <SelectItem key={st.alias} value={st.alias}>{st.alias}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Select value={jc.left_field} onValueChange={(v) => updateJoinCondition(idx, { left_field: v })}>
                          <SelectTrigger className="w-[130px] text-sm">
                            <SelectValue placeholder="Field" />
                          </SelectTrigger>
                          <SelectContent>
                            {leftFields.map((f) => (
                              <SelectItem key={f.name || f.target_field} value={f.name || f.target_field}>
                                {f.name || f.target_field}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <span className="text-muted-foreground font-bold">=</span>
                        
                        <Select value={jc.right_table} onValueChange={(v) => updateJoinCondition(idx, { right_table: v, right_field: "" })}>
                          <SelectTrigger className="w-16 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {sourceTables.map((st) => (
                              <SelectItem key={st.alias} value={st.alias}>{st.alias}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Select value={jc.right_field} onValueChange={(v) => updateJoinCondition(idx, { right_field: v })}>
                          <SelectTrigger className="w-[130px] text-sm">
                            <SelectValue placeholder="Field" />
                          </SelectTrigger>
                          <SelectContent>
                            {rightFields.map((f) => (
                              <SelectItem key={f.name || f.target_field} value={f.name || f.target_field}>
                                {f.name || f.target_field}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeJoinCondition(idx)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                  
                  <Button variant="outline" size="sm" onClick={addJoinCondition}>
                    <Plus className="w-4 h-4 mr-1" /> Add Join
                  </Button>
                </div>
              </Section>
            )}
            
            {/* Section 3: Output Fields */}
            <Section 
              number={sourceTables.filter(st => st.role !== 'union').length > 1 ? "3" : "2"} 
              title="Output Fields" 
              icon={ArrowRight}
              badge={fields.length > 0 ? `${fields.length} fields` : null}
            >
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-xs text-muted-foreground">
                    Configure output columns with optional aggregations and transformations.
                  </p>
                  {availableFields.length > 0 && fields.length === 0 && (
                    <Button variant="secondary" size="sm" onClick={addAllFields} className="text-xs">
                      <Sparkles className="w-3 h-3 mr-1" /> Add All Fields
                    </Button>
                  )}
                </div>
                
                {fields.length === 0 && availableFields.length === 0 && (
                  <div className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg">
                    Select source tables first to see available fields.
                  </div>
                )}
                
                {fields.map((field, idx) => (
                  <FieldRow
                    key={idx}
                    field={field}
                    index={idx}
                    availableFields={availableFields}
                    onUpdate={updateField}
                    onRemove={removeField}
                  />
                ))}
                
                <Button variant="outline" size="sm" onClick={addField} disabled={availableFields.length === 0}>
                  <Plus className="w-4 h-4 mr-1" /> Add Field
                </Button>
              </div>
            </Section>
            
            {/* Section 4: Calculated Fields */}
            <Section 
              number={sourceTables.filter(st => st.role !== 'union').length > 1 ? "4" : "3"} 
              title="Calculated Fields" 
              icon={Calculator}
              defaultOpen={calculatedFields.length > 0}
              badge={calculatedFields.length > 0 ? `${calculatedFields.length} fields` : 'optional'}
            >
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Create custom calculated fields using SQL expressions.
                </p>
                
                {calculatedFields.map((cf, idx) => (
                  <div key={idx} className="flex gap-2 items-center bg-white p-2 rounded-lg border">
                    <Input
                      value={cf.name || ""}
                      onChange={(e) => updateCalculatedField(idx, { name: e.target.value })}
                      placeholder="Field name"
                      className="w-[150px] text-sm"
                    />
                    <span className="text-muted-foreground">=</span>
                    <Input
                      value={cf.expression || ""}
                      onChange={(e) => updateCalculatedField(idx, { expression: e.target.value })}
                      placeholder="e.g., SAFE_DIVIDE(SUM(profit), NULLIF(SUM(revenue), 0))"
                      className="flex-1 font-mono text-sm"
                    />
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeCalculatedField(idx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                <Button variant="outline" size="sm" onClick={addCalculatedField}>
                  <Plus className="w-4 h-4 mr-1" /> Add Calculation
                </Button>
              </div>
            </Section>
            
            {/* Section 5: Advanced Options */}
            <Section 
              number={sourceTables.filter(st => st.role !== 'union').length > 1 ? "5" : "4"} 
              title="Advanced Options" 
              icon={Settings}
              defaultOpen={false}
              badge="optional"
            >
              <div className="space-y-4">
                {/* Target Table Name */}
                <div className="space-y-2">
                  <Label className="text-sm">Target Table Name</Label>
                  <Input
                    value={stage.target_table || ""}
                    onChange={(e) => onUpdate({ ...stage, target_table: e.target.value })}
                    placeholder="e.g., sales_forecast_daily"
                    className="text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Partition By */}
                  <div className="space-y-2">
                    <Label className="text-sm">Partition By</Label>
                    <Input
                      value={stage.partition_by || ""}
                      onChange={(e) => onUpdate({ ...stage, partition_by: e.target.value })}
                      placeholder="e.g., period_date"
                      className="text-sm font-mono"
                    />
                  </div>
                  
                  {/* Cluster By */}
                  <div className="space-y-2">
                    <Label className="text-sm">Cluster By</Label>
                    <Input
                      value={stage.cluster_by || ""}
                      onChange={(e) => onUpdate({ ...stage, cluster_by: e.target.value })}
                      placeholder="e.g., tenant_id, unit_id"
                      className="text-sm font-mono"
                    />
                  </div>
                </div>
                
                {/* WHERE Filter */}
                <div className="space-y-2">
                  <Label className="text-sm">WHERE Filter</Label>
                  <Input
                    value={stage.filter_condition || ""}
                    onChange={(e) => onUpdate({ ...stage, filter_condition: e.target.value })}
                    placeholder="e.g., status = 'ACTIVE' AND amount > 0"
                    className="text-sm font-mono"
                  />
                </div>
                
                {/* GROUP BY Override */}
                <div className="space-y-2">
                  <Label className="text-sm">GROUP BY (auto-detected if empty)</Label>
                  <Input
                    value={(stage.group_by_fields || []).join(', ')}
                    onChange={(e) => onUpdate({ 
                      ...stage, 
                      group_by_fields: e.target.value ? e.target.value.split(',').map(s => s.trim()) : [] 
                    })}
                    placeholder="Auto-detected from non-aggregated fields"
                    className="text-sm font-mono"
                  />
                </div>
              </div>
            </Section>
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
});

GoldStageCardV2.displayName = 'GoldStageCardV2';

export default GoldStageCardV2;
